import type { AgentClarificationContext } from '../types/agent';

export type ClarificationActionStateReason =
  | 'not_waiting'
  | 'in_flight'
  | 'no_pending_actions'
  | 'expired_tokens'
  | 'ok'
  | string
  | undefined;

export interface ClarificationOverlayStateInput {
  currentStatus: string;
  executionStatus: string;
  clarificationContext: AgentClarificationContext | null;
  clarificationOptions: any[];
  pendingActions: any[];
  serverClarificationActionState?: {
    actionable?: boolean;
    reason?: ClarificationActionStateReason;
  } | null;
  clarificationActionInFlightId?: string | null;
  serverClarificationActionInFlightId?: string | null;
  actionMutationPending?: boolean;
  suppressedClarificationContextId?: string | null;
  suppressedClarificationContextUpdatedAt?: string | null;
  suppressedClarificationStatus?: string | null;
  holdSuppressedClarification?: boolean;
  clarificationRecoveryAutoRefreshing?: boolean;
  localTransitionReleaseAuthorized?: boolean;
  localTransitionInProgress?: boolean;
  isExecutingStatus?: boolean;
  isApprovalDecisionOverlayState?: boolean;
  changeGoalHandoffPending?: boolean;
}

export interface ClarificationOverlayState {
  isWaitingClarificationState: boolean;
  isDecisionOverlayState: boolean;
  activeClarificationContextId: string;
  effectiveClarificationContext: AgentClarificationContext | null;
  hasClarificationContext: boolean;
  executionInProgress: boolean;
  isClarificationOverlayActive: boolean;
  chatLockedForClarification: boolean;
  isClarificationUiLocked: boolean;
  clarificationDisabledReason: string;
  shouldReleaseSuppression: boolean;
  isSuppressed: boolean;
  hasServerClarificationActionState: boolean;
  hasAuthoritativeBlockingClarification: boolean;
}

const OVERRIDABLE_BLOCKING_CLARIFICATION_STATUSES = new Set([
  'pending_approval',
  'plan_pending_approval',
  'analyzing',
  'generating_plan',
  'regenerating_plan',
  'cloning',
]);

const NON_OVERRIDABLE_EXECUTION_STATUSES = new Set([
  'executing',
  'plan_approved',
  'approved',
]);

const PLAN_APPROVAL_ACTIONS = new Set(['approve_plan', 'reject_plan']);

function normalizeActionValue(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

function mapClarificationDisabledReason(
  reason?: ClarificationActionStateReason,
  fallbackWaiting = false,
  executionInProgress = false,
  isSyntheticRefresh = false
): string {
  if (reason === 'in_flight') return 'Applying your selected action...';
  if (reason === 'no_pending_actions') return 'Updating options...';
  if (reason === 'expired_tokens') return 'Updating options...';
  if (reason === 'not_waiting') return 'No pending clarification for this task.';
  if (isSyntheticRefresh) return 'Refreshing decision options...';
  if (executionInProgress) return 'Agent is working...';
  if (!fallbackWaiting) return 'No pending clarification for this task.';
  return 'Processing selected action...';
}

type ClarificationResolutionState = {
  activeClarificationContextId: string;
  effectiveClarificationContext: AgentClarificationContext | null;
  hasClarificationContext: boolean;
  hasServerClarificationActionState: boolean;
  isWaitingClarificationState: boolean;
  isDecisionOverlayState: boolean;
  explicitPausedDecisionLane: boolean;
  allowClarificationRecovery: boolean;
  localOrServerInFlight: boolean;
  executionInProgress: boolean;
  shouldReleaseSuppression: boolean;
  isSuppressed: boolean;
  hasAuthoritativeBlockingClarification: boolean;
  isSyntheticClarificationRefreshContext: boolean;
};

function deriveClarificationResolutionState(
  input: ClarificationOverlayStateInput
): ClarificationResolutionState {
  const currentStatus = String(input.currentStatus || '').trim().toLowerCase();
  const executionStatusForOverlay = String(input.executionStatus || '')
    .trim()
    .toLowerCase();
  const clarificationContext = input.clarificationContext || null;
  const question =
    clarificationContext && typeof clarificationContext.question === 'string'
      ? clarificationContext.question
      : '';
  const isSyntheticClarificationRefreshContext =
    !!question && /updating decision context/i.test(question);
  const effectiveClarificationContext = isSyntheticClarificationRefreshContext
    ? null
    : clarificationContext;
  const activeClarificationContextId =
    effectiveClarificationContext && typeof effectiveClarificationContext.id === 'string'
      ? effectiveClarificationContext.id.trim()
      : '';
  const activeClarificationContextUpdatedAt =
    effectiveClarificationContext && typeof effectiveClarificationContext.updatedAt === 'string'
      ? effectiveClarificationContext.updatedAt.trim()
      : '';
  const hasClarificationContext =
    !!effectiveClarificationContext &&
    typeof effectiveClarificationContext.question === 'string' &&
    effectiveClarificationContext.question.trim().length > 0;
  const hasServerClarificationActionState =
    typeof input.serverClarificationActionState?.actionable === 'boolean';
  const hasClarificationOptions =
    Array.isArray(input.clarificationOptions) && input.clarificationOptions.length > 0;
  const hasPendingActions =
    Array.isArray(input.pendingActions) && input.pendingActions.length > 0;
  const clarificationPayloadActionValues = new Set<string>([
    ...(Array.isArray(input.clarificationOptions)
      ? input.clarificationOptions.map((option) => normalizeActionValue(option?.value))
      : []),
    ...(Array.isArray(input.pendingActions)
      ? input.pendingActions.map((action) =>
          normalizeActionValue(action?.value || action?.actionValue)
        )
      : []),
  ]);
  const hasClarificationSignalFromPayload =
    !!activeClarificationContextId || hasClarificationOptions || hasPendingActions;
  const statusIndicatesWaiting =
    currentStatus === 'waiting_clarification' ||
    executionStatusForOverlay === 'waiting_clarification' ||
    executionStatusForOverlay === 'awaiting_input';
  const isWaitingClarificationState = hasServerClarificationActionState
    ? input.serverClarificationActionState?.actionable === true ||
      input.serverClarificationActionState?.reason === 'in_flight' ||
      statusIndicatesWaiting ||
      hasClarificationSignalFromPayload
    : statusIndicatesWaiting || hasClarificationSignalFromPayload;
  const isDecisionOverlayState =
    isWaitingClarificationState || input.isApprovalDecisionOverlayState === true;
  const explicitPausedDecisionLane =
    (executionStatusForOverlay === 'waiting_clarification' ||
      executionStatusForOverlay === 'awaiting_input') &&
    (hasClarificationSignalFromPayload ||
      input.serverClarificationActionState?.actionable === true);
  const allowClarificationRecovery =
    hasServerClarificationActionState &&
    (input.serverClarificationActionState?.reason === 'expired_tokens' ||
      input.serverClarificationActionState?.reason === 'no_pending_actions');
  const localOrServerInFlight =
    !!input.clarificationActionInFlightId ||
    !!input.serverClarificationActionInFlightId ||
    input.actionMutationPending === true;
  const localTransitionReleaseAuthorized =
    input.localTransitionReleaseAuthorized === true;
  const localTransitionInProgress =
    !localTransitionReleaseAuthorized &&
    (input.localTransitionInProgress === true ||
      input.holdSuppressedClarification === true);
  const hasActionableClarificationPayload =
    input.serverClarificationActionState?.actionable === true ||
    hasClarificationOptions ||
    hasPendingActions;
  const hasOverridableExecutionLane =
    OVERRIDABLE_BLOCKING_CLARIFICATION_STATUSES.has(currentStatus) ||
    OVERRIDABLE_BLOCKING_CLARIFICATION_STATUSES.has(executionStatusForOverlay);
  const hasNonOverridableExecutionLane =
    NON_OVERRIDABLE_EXECUTION_STATUSES.has(currentStatus) ||
    NON_OVERRIDABLE_EXECUTION_STATUSES.has(executionStatusForOverlay);
  const clarificationPhase =
    effectiveClarificationContext && typeof effectiveClarificationContext.phase === 'string'
      ? effectiveClarificationContext.phase.trim().toLowerCase()
      : '';
  const hasPlanApprovalPayload =
    clarificationPhase === 'plan_approval' ||
    Array.from(clarificationPayloadActionValues).some((value) =>
      PLAN_APPROVAL_ACTIONS.has(value)
    );
  const shouldDeferStaleChangeGoalClarification =
    input.changeGoalHandoffPending === true &&
    !hasPlanApprovalPayload &&
    (hasOverridableExecutionLane ||
      (input.holdSuppressedClarification === true &&
        !localTransitionReleaseAuthorized) ||
      (input.localTransitionInProgress === true &&
        !localTransitionReleaseAuthorized));
  const blockingClarificationCandidate =
    isDecisionOverlayState &&
    hasClarificationContext &&
    hasActionableClarificationPayload &&
    !allowClarificationRecovery &&
    input.clarificationRecoveryAutoRefreshing !== true &&
    !localOrServerInFlight &&
    !localTransitionInProgress &&
    !shouldDeferStaleChangeGoalClarification;
  const activeExecutionLane =
    !input.isApprovalDecisionOverlayState &&
    !explicitPausedDecisionLane &&
    (hasNonOverridableExecutionLane ||
      (hasOverridableExecutionLane && !blockingClarificationCandidate) ||
      (input.isExecutingStatus === true && !hasOverridableExecutionLane));
  const executionInProgress =
    activeExecutionLane ||
    (!localTransitionReleaseAuthorized &&
      input.holdSuppressedClarification === true);
  const suppressionSnapshotAdvanced =
    !!activeClarificationContextUpdatedAt &&
    !!input.suppressedClarificationContextUpdatedAt &&
    input.suppressedClarificationContextUpdatedAt !== activeClarificationContextUpdatedAt;
  const suppressedSameContext =
    !!activeClarificationContextId &&
    input.suppressedClarificationContextId === activeClarificationContextId;
  const hasComparableSuppressionTimestamps =
    !!activeClarificationContextUpdatedAt &&
    !!input.suppressedClarificationContextUpdatedAt;
  const statusChangedSinceSuppression =
    !input.suppressedClarificationStatus ||
    input.suppressedClarificationStatus !== currentStatus;
  const canReleaseSameContextSuppressionWithoutSnapshotAdvance =
    suppressedSameContext &&
    !hasComparableSuppressionTimestamps &&
    statusChangedSinceSuppression &&
    isWaitingClarificationState &&
    !executionInProgress &&
    !localOrServerInFlight &&
    input.holdSuppressedClarification !== true;
  const shouldReleaseSuppression =
    (localTransitionReleaseAuthorized &&
      isWaitingClarificationState &&
      hasActionableClarificationPayload &&
      !localOrServerInFlight) ||
    (canReleaseSameContextSuppressionWithoutSnapshotAdvance &&
      input.changeGoalHandoffPending !== true) ||
    (suppressedSameContext &&
      suppressionSnapshotAdvanced &&
      isWaitingClarificationState &&
      !executionInProgress &&
      !localOrServerInFlight &&
      input.holdSuppressedClarification !== true) ||
    (suppressedSameContext &&
      (input.changeGoalHandoffPending === true &&
        hasPlanApprovalPayload &&
        isWaitingClarificationState &&
        !executionInProgress &&
        !localOrServerInFlight &&
        input.holdSuppressedClarification !== true));
  const isSuppressed =
    !!activeClarificationContextId &&
    input.suppressedClarificationContextId === activeClarificationContextId &&
    !localTransitionReleaseAuthorized &&
    (input.holdSuppressedClarification === true || !shouldReleaseSuppression);
  const hasAuthoritativeBlockingClarification =
    blockingClarificationCandidate && !activeExecutionLane;

  return {
    activeClarificationContextId,
    effectiveClarificationContext,
    hasClarificationContext,
    hasServerClarificationActionState,
    isWaitingClarificationState,
    isDecisionOverlayState,
    explicitPausedDecisionLane,
    allowClarificationRecovery,
    localOrServerInFlight,
    executionInProgress,
    shouldReleaseSuppression,
    isSuppressed,
    hasAuthoritativeBlockingClarification,
    isSyntheticClarificationRefreshContext,
  };
}

export function hasAuthoritativeBlockingClarification(
  input: ClarificationOverlayStateInput
): boolean {
  return deriveClarificationResolutionState(input).hasAuthoritativeBlockingClarification;
}

export function deriveClarificationOverlayState(
  input: ClarificationOverlayStateInput
): ClarificationOverlayState {
  const {
    activeClarificationContextId,
    effectiveClarificationContext,
    hasClarificationContext,
    hasServerClarificationActionState,
    isWaitingClarificationState,
    isDecisionOverlayState,
    allowClarificationRecovery,
    localOrServerInFlight,
    executionInProgress,
    shouldReleaseSuppression,
    isSuppressed,
    hasAuthoritativeBlockingClarification,
    isSyntheticClarificationRefreshContext,
  } = deriveClarificationResolutionState(input);
  const hasServerUsableOptions =
    hasClarificationContext && Array.isArray(input.clarificationOptions) && input.clarificationOptions.length > 0;
  const serverClarificationLocked = hasServerClarificationActionState
    ? (!input.serverClarificationActionState?.actionable &&
        !allowClarificationRecovery &&
        !hasServerUsableOptions)
    : !isWaitingClarificationState;
  const localTransitionInProgressForOverlay =
    input.localTransitionInProgress === true &&
    input.localTransitionReleaseAuthorized !== true;
  const isClarificationOverlayActive =
    isDecisionOverlayState &&
    !!effectiveClarificationContext &&
    !isSuppressed &&
    !executionInProgress &&
    !localTransitionInProgressForOverlay &&
    !localOrServerInFlight;
  const chatLockedForClarification = isClarificationOverlayActive || executionInProgress || localOrServerInFlight || localTransitionInProgressForOverlay;
  const isClarificationUiLocked =
    executionInProgress || serverClarificationLocked || localOrServerInFlight || localTransitionInProgressForOverlay;
  const clarificationDisabledReason = mapClarificationDisabledReason(
    input.serverClarificationActionState?.reason,
    isWaitingClarificationState,
    executionInProgress,
    isSyntheticClarificationRefreshContext
  );

  return {
    isWaitingClarificationState,
    isDecisionOverlayState,
    activeClarificationContextId,
    effectiveClarificationContext,
    hasClarificationContext,
    executionInProgress,
    isClarificationOverlayActive,
    chatLockedForClarification,
    isClarificationUiLocked,
    clarificationDisabledReason,
    shouldReleaseSuppression,
    isSuppressed,
    hasServerClarificationActionState,
    hasAuthoritativeBlockingClarification,
  };
}
